import logger from './logger';
/**
 * Centralized session storage keys.
 * Used by: CartContext slices, resetAppointment, hydration.
 * Single source of truth for storage key names.
 */
export const STORAGE_KEYS = {
  // Appointment flow
  APPOINTMENT_TYPE: 'appointment_type',

  // Location
  OUTLET_TYPE: 'outlet_type',
  SELECTED_OUTLET_ID: 'selected_outlet_id',
  SELECTED_OUTLET_CONTACT: 'selected_outlet_contact',
  SELECTED_OUTLET_WP: 'selected_outlet_wp',

  // Guests
  UPDATED_MEMBERS: 'updated_Members',
  ACTIVE_GUEST_ID: 'active_guest_id',
  PRIMARY_CUSTOMER: 'primary_customer',
  MEMBER_COUNT: 'memberCount',

  // Schedule
  DATE: 'date',
  TIME: 'time',
  SLOT_PUBLIC_HOLIDAY: 'slotPublicHoliday',
  DATE_SELECTION_SOURCE: 'date_selection_source',

  // Customer
  FORM_DATA: 'formData',
  USER_VERIFIED: 'userVerified',
  VERIFIED_MOBILE: 'verifiedMobile',
  BLOCKED_CUSTOMER_MOBILE: 'blockedCustomerMobile', // Legacy flag: clear it, never use it to infer account status.
  OTP_CHALLENGE: 'bookingOtpChallenge',
  OTP_ATTEMPTS: 'bookingOtpAttempts', // Legacy local counter; no longer used.
  OTP_API_ATTEMPTS: 'bookingOtpApiAttempts',
  USER_ID: 'userId',
  MEMBERSHIP: 'membership',

  // Booking session
  BOOKING_ID: 'bookingId',
  BOOKING_UUID: 'uuid',
  OCCUPIED_TIME: 'occupiedTime',
  BOOKING_DONE: 'isBookingDone',
  BOOKING_UPDATE: 'bookingUpdate',
  RESCHEDULE_COUNT: 'rescheduleCount',
  RESCHEDULE_MAX_LIMIT: 'rescheduleMaxLimit',
  RESCHEDULE_TIME_LIMIT: 'rescheduleTimeLimit',
  AVAILABILITY_BLOCK: 'bookingAvailabilityBlock',

  // Timer
  COUNTDOWN_END_TIME: 'countdownEndTime',
  TIMER_ACTIVE: 'timerActive',
  TIMER_EXTEND: 'timerExtend',
  TIMES_UP: 'timesUp',

  // Deprecated (delete from storage during reset):
  // These are computed from other state and no longer stored.
  SELECTED_SERVICES: 'selected_services',
  SERVICE_COUNT: 'serviceCount',
  CART_TOTAL: 'cartTotal',
  SERVICE_LIST: 'serviceList_1',
  THERAPIST_DATA: 'therapistData',
  OUTLET_TYPE_LIST: 'outletTypeList',
  HAS_SERVICE_WITHOUT_THERAPIST: 'hasServiceWithoutTherapist',
  LOCATION_SELECT_FIRST: 'locationSelectFirst',
  TIME_SELECT_FIRST: 'timeSelectFirst',
};

export const VOUCHER_SESSION_KEYS = {
  selectedVouchers: 'selected_vouchers',
  selectedVoucherIds: 'selected_voucher_ids',
  voucherCount: 'voucher_count',
  voucherTotal: 'voucher_total',
  voucherList: 'voucher_list',
  billingDetails: 'voucher_billing_details',
  recipientDetails: 'voucher_recipient_details',
  giftMessage: 'voucher_gift_message',
  orderData: 'voucher_order_data',
  orderMessage: 'voucher_order_message',
};

/**
 * Get value from sessionStorage (persists only for current tab session).
 * Automatically parses JSON values.
 */
export const getSessionStorageValue = (key, defaultValue = null) => {
  try {
    const value = sessionStorage.getItem(key);
    if (value === null) {
      return defaultValue;
    }
    try {
      return JSON.parse(value);
    } catch {
      // Backward compatibility for older plain-string storage values.
      return value;
    }
  } catch (error) {
    logger.error(`Error getting sessionStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Set value in sessionStorage (persists only for current tab session).
 * Automatically serializes values to JSON.
 */
export const setSessionStorageValue = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    logger.error(`Error setting sessionStorage key "${key}":`, error);
  }
};

/**
 * Remove value from sessionStorage.
 */
export const removeSessionStorageValue = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    logger.error(`Error removing sessionStorage key "${key}":`, error);
  }
};

/**
 * Clear all booking-related data from sessionStorage.
 * Preserves the reusable UUID, user authentication and non-booking data.
 */
export const clearSessionStorage = ({ preserveUuid = true } = {}) => {
  try {
    const bookingKeys = Object.values(STORAGE_KEYS);
    bookingKeys.forEach((key) => {
      if (preserveUuid && key === STORAGE_KEYS.BOOKING_UUID) return;
      sessionStorage.removeItem(key);
    });

    sessionStorage.removeItem('adyen-checkout__session');
    sessionStorage.removeItem('service_list');
  } catch (error) {
    logger.error('Error clearing booking data:', error);
  }
};

export const clearBookingFlowStorage = () => {
  clearSessionStorage({ preserveUuid: true });
  Object.values(VOUCHER_SESSION_KEYS).forEach(removeSessionStorageValue);
};
